import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import lset from "lodash/set";
import { updateProp, addRecords, removeRecords } from "#src/api";
import {
  hydrateItem,
  hydrateType,
  updateItemCache,
  updateTypeCache,
  updateMaterialCache,
} from "@local/lamina-core";

import {
  addToSortableList,
  removeFromSortableList,
} from "@local/extensions/collections/sortable-list.js";

function matDbProps(mat) {
  if (!mat || mat.type === "none") {
    return { type: "none" };
  } else if (!mat.type) {
    return mat;
  } else if (mat.type === "fixed") {
    return mat;
  } else if (mat.type === "query") {
    const { type, value, default: def } = mat;
    return { type, value, default: def };
  } else if (mat.type === "option") {
    const { type, options, default: def } = mat;
    return { type, options, default: def };
  } else if (mat.type === "reference") {
    const { type, value } = mat;
    return { type, value };
  } else {
    return mat;
  }
}

function removeNonDbProps(item) {
  const { approver, updater, cache, ...rest } = item;
  return rest;
}

function removeProps(recName, records) {
  if (recName === "locations") {
    return records.map((loc) => {
      const { position_pt, ...rest } = loc;
      return rest;
    });
  }

  return records;
}

export function remExtdItemProdProps(itemProduct) {
  const {
    category_name,
    category_image,
    expanded_data,
    item_script,
    item_script_decl,
    ...rest
  } = itemProduct;
  return rest;
}

export function remExtdMakeupProps(makeup) {
  const {
    data,
    cache,
    expanded_data,
    _orig_data,
    category_image,
    category_name,
    ...rest
  } = makeup;
  const layers = data?.layers.map((l) => {
    return {
      ...l,
      material: matDbProps(l.material),
      ...(l.inboard_surface
        ? { inboard_surface: matDbProps(l.inboard_surface) }
        : {}),
      ...(l.outboard_surface
        ? { outboard_surface: matDbProps(l.outboard_surface) }
        : {}),
    };
  });
  return {
    ...rest,
    ...(data ? { data: { ...data, layers } } : {}),
  };
}

function updateSync(record, update) {
  let oldValue = {};
  let newValue = {};

  if (update.diff) {
    oldValue = Object.keys(update.diff).reduce((o, p) => {
      const path = p.split(".");
      const [col] = path;
      o[col] = record[col];
      return o;
    }, {});

    newValue = Object.keys(update.diff).reduce((o, p) => {
      const path = p.split(".");
      const [col, ...rest] = path;

      if (["data", "custom_column_data"].includes(col)) {
        if (rest.length > 0) {
          if (!o[col]) o[col] = cloneDeep(record[col]);
          lset(o[col], rest, update.diff[p]);
        } else {
          o[col] = update.diff[p];
        }
      } else {
        o[p] = update.diff[p];
      }

      return o;
    }, {});
  } else if (update.prop) {
    const path = update.prop.split(".");
    const [col, ...rest] = path;

    if (["data", "custom_column_data"].includes(col) && rest.length > 0) {
      oldValue = { [col]: record[col] };
      const val = cloneDeep(record[col]);
      lset(val, rest, update.value);
      newValue = { [col]: val };
    } else {
      oldValue = { [col]: record[col] };
      newValue = { [col]: update.value };
    }
  }

  Object.entries(newValue).forEach(([key, value]) => {
    record[key] = value;
  });

  if (record.hasOwnProperty("updated_at")) {
    record.updated_at = new Date().toISOString();
  }

  return [oldValue, newValue];
}

function add(n, up, profile) {
  let callback;
  let undoSync;
  let undoAsync;
  if (up.type === "type") {
    const ids = up.records.map((t) => t.id);
    const old_data = cloneDeep(n.data);
    old_data.type_order = [...n.types.order];
    addToSortableList(n.types, ...up.records.map((t) => hydrateType(t)));
    n.data.type_order = [...n.types.order];

    callback = async () => {
      await addRecords(
        "types",
        up.records.map((t) => removeNonDbProps(t)),
      );
      await updateProp("groups", n.id, "data", n.data);
    };

    undoSync = (n) => {
      removeFromSortableList(n.types, ...ids);
    };

    undoAsync = async (n) => {
      await removeRecords("types", ids);
      await updateProp("groups", n.id, "data", old_data);
    };
  } else if (up.type === "item" || up.type === "collection") {
    const ids = up.records.map((i) => i.id);
    const old_data = cloneDeep(n.data);
    old_data.item_order = [...n.items.order];
    addToSortableList(n.items, ...up.records.map((i) => hydrateItem(i, n)));
    n.data.item_order = [...n.items.order];

    callback = async () => {
      await addRecords(
        "items",
        up.records.map((i) => removeNonDbProps(i)),
      );
      await updateProp("groups", n.id, "data", n.data);
    };

    undoSync = (n) => {
      removeFromSortableList(n.items, ...ids);
    };

    undoAsync = async (n) => {
      await removeRecords("items", ids);
      await updateProp("groups", n.id, "data", old_data);
    };
  } else {
    const recName = `${up.type}s`;
    const ids = up.records.map((r) => r.id);
    addToSortableList(n[recName], ...up.records);
    const stored = removeProps(recName, up.records);

    callback = async () => addRecords(recName, stored);

    undoSync = (n) => {
      removeFromSortableList(n[recName], ...ids);
    };

    undoAsync = async (n) => {
      await removeRecords(recName, ids);
    };
  }

  return [callback, undoSync, undoAsync];
}

function remove(n, up) {
  let callback;
  let undoSync;
  let undoAsync;
  if (up.type === "type") {
    const types = up.ids.map((id) => n.types[id]);
    removeFromSortableList(n.types, ...up.ids);
    n.data.type_order = [...n.types.order];

    callback = async () => {
      await removeRecords("types", up.ids);
      await updateProp("groups", n.id, "data", n.data);
    };

    undoSync = (n) => {
      addToSortableList(n.types, ...types);
      const data = n.data;
      data.type_order = [...n.types.order];
    };

    undoAsync = async (n) => {
      await addRecords(
        "types",
        types.map((t) => removeNonDbProps(t)),
      );
      await updateProp("groups", n.id, "data", n.data);
    };
  } else if (up.type === "item") {
    const items = up.ids.map((id) => n.items[id]);
    removeFromSortableList(n.items, ...up.ids);
    n.data.item_order = [...n.items.order];

    callback = async () => {
      await removeRecords("items", up.ids);
      await updateProp("groups", n.id, "data", n.data);
    };

    undoSync = (n) => {
      addToSortableList(n.items, ...items);
      const data = n.data;
      data.item_order = [...n.items.order];
    };

    undoAsync = async (n) => {
      await addRecords(
        "items",
        items.map((i) => removeNonDbProps(i)),
      );
      await updateProp("groups", n.id, "data", n.data);
    };
  } else if (up.type === "quote_item") {
    const qiObj = up.ids.reduce((obj, id) => {
      obj[id] = true;
      return obj;
    }, {});
    const quoteItems = n.quote_items.filter((qi) => qiObj[qi.id]);
    n.quote_items = n.quote_items.filter((qi) => !qiObj[qi.id]);

    callback = async () => removeRecords("quote_items", up.ids);

    undoSync = (n) => {
      n.quote_items.push(...quoteItems);
    };

    undoAsync = async (n) => {
      await addRecords("quote_items", quoteItems);
    };
  } else {
    const recName = `${up.type}s`;
    const records = up.ids.map((id) => n[recName][id]);
    removeFromSortableList(n[recName], ...up.ids);

    callback = async () => removeRecords(recName, up.ids);

    undoSync = (n) => {
      const stored = removeProps(recName, records);
      addToSortableList(n[recName], ...stored);
    };

    undoAsync = async (n) => {
      const stored = removeProps(recName, records);
      await addRecords(recName, stored);
    };
  }

  return [callback, undoSync, undoAsync];
}

function applyUpdate(record, update) {
  Object.entries(update).forEach(([key, value]) => {
    record[key] = value;
  });
  if (record.hasOwnProperty("updated_at")) {
    record.updated_at = new Date().toISOString();
  }
}

function multiUpdateSync(records, update) {
  let ov, nv;
  update.id.forEach((id) => {
    const record = records[id];
    [ov, nv] = updateSync(record, update);
  });
  return [ov, nv];
}

function update(n, up, profile) {
  let callback;
  let undoSync;
  let undoAsync;
  if (up.type === "group") {
    const [ov, nv] = updateSync(n, up);

    callback = async () => updateProp("groups", n.id, nv);

    undoSync = (n) => {
      applyUpdate(n, ov);
    };

    undoAsync = async (n) => {
      await updateProp("groups", n.id, ov);
    };
  } else if (up.type === "organization") {
    const [ov, nv] = updateSync(n, up);

    callback = async () => updateProp("organizations", n.id, nv);

    undoSync = (n) => {
      applyUpdate(n, ov);
    };

    undoAsync = async (n) => {
      await updateProp("organzations", n.id, ov);
    };
  } else if (up.type === "item" || up.type === "collection") {
    if (Array.isArray(up.id)) {
      let ov, nv;
      up.id.forEach((id) => {
        const item = n.items[id];
        [ov, nv] = updateSync(item, up);
        item.updater = profile;
        updateItemCache(item, n);
      });

      callback = async () => updateProp("items", up.id, nv);

      undoSync = (n) => {
        up.id.forEach((id) => {
          const item = n.items[id];
          if (!item) return;
          applyUpdate(item, cloneDeep(ov));
          item.updater = profile;
          updateItemCache(item, n);
        });
      };

      undoAsync = async (n) => {
        await updateProp("items", up.id, ov);
      };
    } else {
      const item = n.items[up.id];
      const [ov, nv] = updateSync(item, up);
      item.updater = profile;
      updateItemCache(item, n);

      callback = async () => updateProp("items", up.id, nv);

      undoSync = (n) => {
        const item = n.items[up.id];
        if (!item) return;
        applyUpdate(item, ov);
        item.updater = profile;
        updateItemCache(item, n);
      };

      undoAsync = async (n) => {
        await updateProp("items", up.id, ov);
      };
    }
  } else if (up.type === "type") {
    if (Array.isArray(up.id)) {
      let ov, nv;
      up.id.forEach((id) => {
        const type = n.types[id];
        [ov, nv] = updateSync(type, up);
        type.updater = profile;
        updateTypeCache(type);
      });

      callback = async () => updateProp("types", up.id, nv);

      undoSync = (n) => {
        up.id.forEach((id) => {
          const type = n.types[id];
          if (!type) return;
          applyUpdate(type, cloneDeep(ov));
          type.updater = profile;
          updateTypeCache(type);
        });
      };

      undoAsync = async (n) => {
        await updateProp("types", up.id, ov);
      };
    } else {
      const type = n.types[up.id];
      const [ov, nv] = updateSync(type, up);
      type.updater = profile;
      updateTypeCache(type);

      callback = async () => updateProp("types", up.id, nv);

      undoSync = (n) => {
        const type = n.types[up.id];
        if (!type) return;
        applyUpdate(type, ov);
        type.updater = profile;
        updateTypeCache(type);
      };

      undoAsync = async (n) => {
        await updateProp("types", up.id, ov);
      };
    }
  } else if (up.type === "quote_item") {
    const quoteItem = n.quote_items.find((qi) => qi.id === up.id);
    const [ov, nv] = updateSync(quoteItem, up);

    callback = async () => updateProp("quote_items", up.id, nv);

    undoSync = (n) => {
      const quoteItem = n.quote_items.find((qi) => qi.id === up.id);
      if (!quoteItem) return;
      applyUpdate(quoteItem, ov);
    };

    undoAsync = async (n) => {
      await updateProp("quote_items", up.id, ov);
    };
  } else if (up.type === "item_product") {
    const itemProduct = n.item_products[up.id];
    const [ov, nv] = updateSync(itemProduct, up);

    callback = async () =>
      updateProp("product_entries", up.id, remExtdItemProdProps(nv));

    undoSync = (n) => {
      const itemProduct = n.item_products[up.id];
      if (!itemProduct) return;
      applyUpdate(itemProduct, ov);
    };

    undoAsync = async (n) => {
      await updateProp("product_entries", up.id, remExtdItemProdProps(ov));
    };
  } else if (up.type === "material") {
    const material = n.materials[up.id];
    const [ov, nv] = updateSync(material, up);
    updateMaterialCache(material);

    callback = async () => updateProp("product_entries", up.id, nv);

    undoSync = (n) => {
      const material = n.materials[up.id];
      if (!material) return;
      applyUpdate(material, ov);
      updateMaterialCache(material);
    };

    undoAsync = async (n) => {
      await updateProp("product_entries", up.id, ov);
    };
  } else if (up.type === "makeup") {
    const makeup = n.makeups[up.id];
    const [ov, nv] = updateSync(makeup, up);
    if (makeup.application === "makeup") {
      updateTypeCache(makeup);
      callback = async () =>
        updateProp("product_entries", up.id, remExtdMakeupProps(nv));

      undoSync = (n) => {
        const makeup = n.makeups[up.id];
        if (!makeup) return;
        applyUpdate(makeup, ov);
        updateTypeCache(makeup);
      };

      undoAsync = async (n) => {
        await updateProp("product_entries", up.id, remExtdMakeupProps(ov));
      };
    } else {
      callback = async () => updateProp("product_entries", up.id, nv);

      undoSync = (n) => {
        const makeup = n.makeups[up.id];
        if (!makeup) return;
        applyUpdate(makeup, ov);
      };

      undoAsync = async (n) => {
        await updateProp("product_entries", up.id, ov);
      };
    }
  } else if (
    ["product_setting", "fabrication", "surface", "surcharge"].includes(up.type)
  ) {
    const record = `${up.type}s`;
    const product = n[record][up.id];
    const [ov, nv] = updateSync(product, up);

    callback = async () => updateProp("product_entries", up.id, nv);

    undoSync = (n) => {
      const product = n[record][up.id];
      if (!product) return;
      applyUpdate(product, ov);
    };

    undoAsync = async (n) => {
      await updateProp("product_entries", up.id, ov);
    };
  } else {
    const recName = `${up.type}s`;
    if (Array.isArray(up.id)) {
      const record = up.id.map((id) => n[recName]?.[id]).filter((r) => r);
      if (record.length === 0) {
        return [() => {}, () => {}];
      }
      const [ov, nv] = multiUpdateSync(n[recName], up);
      callback = async () => updateProp(recName, up.id, nv);

      undoSync = (n) => {
        up.id.forEach((id) => {
          const record = n[recName]?.[id];
          if (!record) return;
          applyUpdate(record, cloneDeep(ov));
        });
      };

      undoAsync = async (n) => {
        await updateProp(recName, up.id, ov);
      };
    } else {
      const record = n[recName]?.[up.id];
      if (!record) {
        return [() => {}, () => {}];
      }
      const [ov, nv] = updateSync(record, up);
      if (record) callback = async () => updateProp(recName, up.id, nv);

      undoSync = (n) => {
        const record = n[recName]?.[up.id];
        if (!record) return;
        applyUpdate(record, ov);
      };

      undoAsync = async (n) => {
        await updateProp(recName, up.id, ov);
      };
    }
  }

  return [callback, undoSync, undoAsync];
}

const actions = { add, remove, update };

async function execSequentially(updates, promises, n) {
  for (const [i, up] of updates.entries()) {
    const cb = promises[i];
    if (up.block) {
      await cb(n);
    } else {
      cb(n);
    }
  }
}

function hasCompositeColumns(update) {
  if (update.diff) {
    const keys = Object.keys(update.diff);
    return keys.some((k) => k.includes("."));
  } else if (update.prop !== undefined) {
    return update.prop.includes(".");
  }
}

function everyUpdateEqual(updates) {
  const [first, ...rest] = updates;
  if (
    (first.action != null && first.action !== "update") ||
    !["location", "item", "type", "document_note"].includes(first.type) ||
    hasCompositeColumns(first)
  )
    return false;

  return rest.every((up) => {
    return (
      first.type === up.type &&
      first.prop === up.prop &&
      !up.await &&
      !up.block &&
      isEqual(first.diff, up.diff) &&
      isEqual(first.value, up.value)
    );
  });
}

export function updateCommit(updates, $p = null) {
  const commit = async (n) => {
    if (updates.length > 1 && everyUpdateEqual(updates)) {
      const first = updates[0];
      const id = updates.map((u) => u.id).flat();
      const up = { ...first, id };
      const [cb, undoSync, undoAsync] = update(n, up, $p);

      cb(n);

      return async (n) => {
        undoSync(n);
        undoAsync(n);
      };
    } else {
      const callbacks = [];
      const undosSync = [];
      const undosAsync = [];
      updates.forEach((up) => {
        const action = actions[up.action] || update;
        const [cb, undoSync, undoAsync] = action(n, up, $p);
        callbacks.push(cb);
        undosSync.push(undoSync);
        undosAsync.push(undoAsync);
      });

      if (updates.some((up) => up.await)) {
        await execSequentially(updates, callbacks);
      } else {
        execSequentially(updates, callbacks);
      }

      return async (n) => {
        undosSync.toReversed().forEach((undo, i) => {
          undo(n);
        });

        let undoUpdates = updates.toReversed();
        if (undoUpdates.some((up) => up.block)) {
          undoUpdates = undoUpdates.map((up) => {
            return { ...up, block: !up.block };
          });
        }

        let uasync = undosAsync.toReversed();

        if (undoUpdates.some((up) => up.await)) {
          await execSequentially(undoUpdates, uasync, n);
        } else {
          execSequentially(undoUpdates, uasync, n);
        }
      };
    }
  };

  return commit;
}

export function addCommit(
  recordType,
  table,
  parentTable,
  records,
  hydrate,
  strip,
) {
  const ids = records.map((t) => t.id);

  const commit = async (n) => {
    const recordList = n[`${recordType}s`];
    const old_data = cloneDeep(n.data);
    old_data[`${recordType}_order`] = cloneDeep(recordList.order);

    if (hydrate) {
      addToSortableList(recordList, ...records.map((t) => hydrate(t)));
    } else {
      addToSortableList(recordList, ...records);
    }
    n.data[`${recordType}_order`] = [...recordList.order];

    await addRecords(table, strip ? records.map((t) => strip(t)) : records);

    updateProp(parentTable, n.id, "data", n.data);

    return async (n) => {
      removeFromSortableList(n[`${recordType}s`], ...ids);
      await removeRecords(table, ids);
      updateProp(parentTable, n.id, "data", old_data);
    };
  };

  return commit;
}

export function removeCommit(
  recordType,
  table,
  parentTable,
  ids,
  hydrate,
  strip,
) {
  const commit = (n, commitId) => {
    const recordList = n[`${recordType}s`];
    const old_data = cloneDeep(n.data);
    old_data[`${recordType}_order`] = [...recordList.order];

    removeFromSortableList(recordList, ...ids);
    n.data[`${recordType}_order`] = [...recordList.order];

    const res = {};
    removeRecords(table, ids).then((r) => (res[`${recordType}s`] = r));
    updateProp(parentTable, n.id, "data", n.data);

    return (n) => {
      const rl = res[`${recordType}s`];
      if (hydrate) {
        addToSortableList(recordList, ...rl.map((t) => hydrateType(t)));
      } else {
        addToSortableList(recordList, ...rl);
      }
      addRecords(table, strip ? rl.map((t) => strip(t)) : rl);
      updateProp(parentTable, n.id, "data", old_data);
    };
  };

  return commit;
}

export function reorderCommit(recordType, parentTable, order) {
  const commit = (n, commitId) => {
    const oldOrder = n[`${recordType}s`].order;
    const oldData = n.data;

    n[`${recordType}s`].order = order;
    const data = cloneDeep(n.data);
    lset(data, `${recordType}_order`, order);

    updateProp(parentTable, n.id, "data", data);

    return (n) => {
      n[`${recordType}s`].order = oldOrder;
      lset(n.data, oldData);
      updateProp(parentTable, n.id, "data", oldData);
    };
  };

  return commit;
}
